import { type ChecklistMarkRow } from "@lib/todoRailTypes";
import { type ProgressRow } from "@lib/videoStateTypes";
import {
  assignableTrackSchema,
  trackNames,
  type AssignableTrack,
} from "@lib/types";
import { isRailOnlyVideoKind, videoKindTodoRail } from "@lib/videoKinds";
import { todoStepIds } from "./todoStepIds";

/**
 * The To Do rail: a round expressed as the ordered walkthrough a funder is
 * meant to move through, with each step carrying its own progress.
 *
 * Everything here is DERIVED. The rail stores almost nothing of its own — the
 * video steps come from the watch-progress rows, "set a score" from the
 * funder's ratings in the round's event log, the scoring and evaluation steps
 * from the same log. Only the two purely observational marks (round notes read,
 * profile summary viewed) have no artifact to infer from, and those are the
 * only things `SProcessUserRoundChecklistMark` holds — and even those are
 * recorded BY THE APP when the funder opens the surface in question
 * (`useRecordTodoObservation`), never ticked by hand.
 *
 * That is why this is a pure function over data the workspace already has,
 * rather than a stored checklist that a background job keeps in step: a stored
 * copy would need invalidating every time a video is uploaded, a rating
 * changes, or a recommender joins — and would be wrong in between.
 */

/** The three phases a round is worked through, in order. */
export type TodoPhase = "watch" | "score" | "allocate";

export const todoPhaseLabels: Record<TodoPhase, string> = {
  watch: "Watch",
  score: "Score",
  allocate: "Allocate",
};

export type TodoStepKind =
  | "video"
  | "recommender"
  /**
   * The collapsible parent holding every recommender. A CONTAINER, not work:
   * see the round-total computation, which counts its children instead of it.
   */
  | "recommenderGroup"
  /**
   * A track, holding its overview and that track's meeting highlights. Also a
   * CONTAINER — see {@link isTodoContainer}.
   */
  | "trackGroup"
  | "observed"
  | "scoring"
  | "evaluations";

/**
 * Whether a step is a container — a row that HOLDS work rather than being work.
 *
 * The round total expands these into what they contain instead of counting
 * them, so collapsing a dozen recommenders (or a track's five videos) into one
 * row stays a rendering decision and never changes what the ring means. A
 * predicate rather than a check against one kind: the first container had that
 * test written inline, and the second one would have inherited a total that
 * silently counted a whole track as a single step.
 */
export function isTodoContainer(step: TodoStep): boolean {
  return step.kind === "recommenderGroup" || step.kind === "trackGroup";
}

export interface TodoStepProgress {
  /** 0–1. Drives the spine fill and the step's share of the round total. */
  fraction: number;
  /**
   * Whether the step counts as finished.
   *
   * For a video this is the server's sticky `completedAt`, NEVER
   * `watchedSeconds / durationSeconds` — the playhead regresses when the
   * funder scrubs back through something they already watched, and a rail that
   * un-ticked itself is precisely the bookmark-management chore this feature
   * exists to remove.
   */
  done: boolean;
  /** Right-aligned figure: "62%", "18/30", "$1.2M". */
  label: string;
  /** The secondary line under the title. */
  detail: string;
}

export interface TodoStep {
  /**
   * Stable identity for this step — an observed step's checklist row stores
   * exactly this string as its `itemKey`, so it must be derived only from
   * durable ids. Minted by `todoStepIds`, which the observing surfaces share
   * so a key can't drift between writer and reader.
   */
  id: string;
  kind: TodoStepKind;
  phase: TodoPhase;
  title: string;
  progress: TodoStepProgress;
  /**
   * Where the step opens, or null when it has no addressable destination (a
   * recommender group header, or a video whose canonical home can't be
   * resolved — see `meetingDeepLink`).
   */
  href: string | null;
  /** Excluded from its parent's progress and from the round total. */
  optional: boolean;
  /** Rows revealed when the step is expanded. */
  children: TodoStep[];
  /** Present on video steps: what the Resume card needs to describe the position. */
  video: TodoStepVideo | null;
  /**
   * How a funder sets this step's state by hand, or null when they can't.
   *
   * This SETS THE REAL VALUE rather than layering a verdict over it: marking a
   * video done writes its completion, marking a summary unread clears the
   * observation. So there is only ever one answer to "is this done", and the
   * automatic machinery reads back exactly what the funder set — no override
   * to remember, nothing to clear, no state that can disagree with itself.
   *
   * Null where the underlying value is real work that can't be asserted: the
   * scoring and evaluation steps come from ratings and allocations in the
   * round's event log, and "set a score" from the funder's own ratings. Ticking
   * those by hand would be claiming work that hasn't happened.
   */
  manual: TodoStepManualTarget | null;
}

/** What a manual "mark as done / not done" writes for a given step. */
export type TodoStepManualTarget =
  /** A video's sticky completion flag. */
  | { kind: "video"; videoId: string }
  /** An observation row, keyed by the step's own id. */
  | { kind: "mark"; itemKey: string };

export interface TodoStepVideo {
  videoId: string;
  watchedSeconds: number;
  durationSeconds: number | null;
  posterId: string | null;
  posterCdnUrl: string | null;
}

/** The catalog fields the rail needs. A subset of `getVideos`' row. */
export interface TodoRailVideo {
  id: string;
  type: string;
  /** Upload status — null for a row that has never been processed. A unit that
   *  is not READY is not yet work the funder can do. */
  status: string | null;
  track: AssignableTrack | null;
  title: string;
  durationSeconds: number | null;
  activePosterId: string | null;
  posterCdnUrl: string | null;
  homeRecommenderId: string | null;
}

export interface TodoRailRecommender {
  recommenderId: string;
  displayName: string;
  /** Whether this recommender wrote general round notes at all — no notes, no step. */
  hasGeneralNotes: boolean;
  /** Whether a profile summary has been generated — no summary, no step. */
  hasProfileSummary: boolean;
  /** The funder has given this recommender at least one manual rating. */
  hasScore: boolean;
}

export interface BuildTodoRailInput {
  roundId: string;
  videos: TodoRailVideo[];
  progress: ProgressRow[];
  marks: ChecklistMarkRow[];
  recommenders: TodoRailRecommender[];
  /** Manual ratings the funder has set, and how many pairs are rateable. */
  scoring: { rated: number; rateable: number };
  /**
   * What the funder has allocated so far, in whole dollars.
   *
   * The sum of the funder's incremental-eval steps — NOT `funder.budget`,
   * which is seeded at round creation and admin-editable, and so reads as a
   * large positive number before anything has been allocated.
   *
   * There is deliberately no denominator: the round holds no target to divide
   * by. The step therefore reports an AMOUNT (which is exactly what the spec
   * asked for) and counts as done once money has been placed, rather than
   * inventing a completion percentage out of a number that doesn't exist.
   */
  evaluations: { allocated: number };
  /** Builds the workspace URL for a video. Injected so the model stays pure. */
  linkToVideo: (video: TodoRailVideo) => string | null;
  /** The recommender page anchored on the General Round Notes card. */
  linkToRecommenderNotes: (recommenderId: string) => string | null;
  /** The recommender page anchored on the Recommender Profile card. */
  linkToRecommenderProfile: (recommenderId: string) => string | null;
  /** The recommender page with an intent to edit Overall Score. */
  linkToRecommenderScore: (recommenderId: string) => string | null;
  linkToScoring: () => string | null;
  linkToEvaluations: () => string | null;
  formatCurrency: (amount: number) => string;
}

export interface TodoRail {
  steps: TodoStep[];
  /** 0–1 across every required step. */
  fraction: number;
  requiredCount: number;
  doneCount: number;
}

const trackOrder: AssignableTrack[] = assignableTrackSchema.options;

/**
 * How many notes a funder is asked to score.
 *
 * A round has far more rateable pairs than anyone is expected to work through —
 * scoring is a SAMPLE, not a sweep — so the rail's denominator is this target,
 * not the total. Using the total made a finished job read as 6% done.
 * A round with fewer rateable pairs than the target asks for all of them.
 */
export const TODO_SCORING_TARGET = 30;

function fractionLabel(fraction: number): string {
  return `${Math.round(fraction * 100)}%`;
}

/**
 * Orders videos by title the way a reader expects: "Meeting #2" before
 * "Meeting #2.5" before "Meeting #10".
 *
 * `numeric: true` is the whole point — a plain `localeCompare` sorts the digits
 * as text, which puts #10 before #2 and reads as scrambled. These titles are
 * numbered by hand, so the numbers are the running order.
 */
function compareByTitle(a: { title: string }, b: { title: string }): number {
  return a.title.localeCompare(b.title, undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

/** "12 min left" — how much is left to watch, which is the useful number. */
function formatRemaining(remainingSeconds: number): string {
  const mins = Math.round(remainingSeconds / 60);
  return mins < 1 ? "Under a minute left" : `${mins} min left`;
}

/**
 * A video step's progress.
 *
 * `done` and `fraction` answer different questions and come from different
 * places on purpose: done is the sticky server flag, fraction is the live
 * playhead (so a part-watched video shows how far in you are). A completed
 * video reports a full bar regardless of where its playhead now sits, so
 * rewinding never visibly undoes it.
 */
function videoProgress(
  video: TodoRailVideo,
  row: ProgressRow | undefined,
): TodoStepProgress {
  const done = row?.completedAt != null;
  const watched = row?.watchedSeconds ?? 0;
  const duration = video.durationSeconds;
  const rawFraction =
    duration != null && duration > 0 ? Math.min(1, watched / duration) : 0;
  const fraction = done ? 1 : rawFraction;

  if (done) {
    return {
      fraction: 1,
      done: true,
      label: "100%",
      detail:
        duration == null
          ? "Watched"
          : `Watched · ${Math.round(duration / 60)} min`,
    };
  }

  if (duration == null) {
    // No duration means no denominator — say so rather than implying 0%.
    return { fraction: 0, done: false, label: "—", detail: "Length unknown" };
  }

  if (watched <= 0) {
    // 0%, not a dash: the dash means "no answer", and "you have watched none of
    // this" is a perfectly good answer. Reserve the dash for genuinely unknown.
    return {
      fraction: 0,
      done: false,
      label: "0%",
      detail: `Not started · ${Math.round(duration / 60)} min`,
    };
  }

  return {
    fraction,
    done: false,
    label: fractionLabel(fraction),
    // What's LEFT, not a pair of timestamps. "1:18 / 34:51" makes the reader do
    // the subtraction to answer the only question they're asking.
    detail: formatRemaining(duration - watched),
  };
}

/**
 * A container's progress, rolled up from the work it holds.
 *
 * Only the REQUIRED children count. A container's own fraction has to mean the
 * same thing as the round total that expands it — and the total drops optional
 * steps — so averaging every child would let four skippable meeting highlights
 * hold a fully-watched track at 20%.
 */
function containerProgress(children: TodoStep[]): {
  required: TodoStep[];
  doneCount: number;
  fraction: number;
  done: boolean;
} {
  const required = children.filter((child) => !child.optional);
  const doneCount = required.filter((child) => child.progress.done).length;

  return {
    required,
    doneCount,
    fraction:
      required.length === 0
        ? 1
        : required.reduce((sum, child) => sum + child.progress.fraction, 0) /
          required.length,
    done: required.every((child) => child.progress.done),
  };
}

function observedStep({
  id,
  title,
  phase,
  href,
  done,
  settable,
}: {
  id: string;
  title: string;
  phase: TodoPhase;
  href: string | null;
  done: boolean;
  /** False for a mark derived from real work (a score), which can't be asserted. */
  settable: boolean;
}): TodoStep {
  return {
    id,
    kind: "observed",
    phase,
    title,
    href,
    optional: false,
    children: [],
    video: null,
    manual: settable ? { kind: "mark", itemKey: id } : null,
    progress: { fraction: done ? 1 : 0, done, label: "", detail: "" },
  };
}

export function buildTodoRail(input: BuildTodoRailInput): TodoRail {
  const progressById = new Map(input.progress.map((row) => [row.videoId, row]));
  const markDone = new Map(
    input.marks.map((mark) => [mark.itemKey, mark.done]),
  );

  const videoStep = (video: TodoRailVideo, optional: boolean): TodoStep => ({
    id: todoStepIds.video(video.id),
    kind: "video",
    phase: "watch",
    title: video.title,
    href: input.linkToVideo(video),
    optional,
    children: [],
    manual: { kind: "video", videoId: video.id },
    progress: videoProgress(video, progressById.get(video.id)),
    video: {
      videoId: video.id,
      watchedSeconds: progressById.get(video.id)?.watchedSeconds ?? 0,
      durationSeconds: video.durationSeconds,
      posterId: video.activePosterId,
      posterCdnUrl: video.posterCdnUrl,
    },
  });

  // --- Steps 1..n: the videos, in rail order -------------------------------
  // Membership and order come from the video-kind table, so a new kind joins
  // the walkthrough by declaring `todoRail` there rather than by being listed
  // here. Kinds with no rail placement (excerpts, raw recordings, onboarding)
  // fall out.
  const railVideos = input.videos.filter(
    (video) =>
      videoKindTodoRail(video.type) != null &&
      // ...and only once the funder can actually watch it. The video catalog
      // lists anything with an uploaded file, while the funder's meeting strips
      // require a curated unit to be READY (`funderUnitOrForSurface`) — so a
      // still-processing or failed overview produced a required step whose page
      // cannot select or play it, clearable only by claiming it was watched.
      // Every kind with a rail placement is a curated one, which is why READY
      // is the whole rule here; a transcript-requiring kind joining the rail
      // would need the transcript arm of that predicate too.
      video.status === "READY",
  );
  const videoSteps = buildVideoRailSteps(railVideos, videoStep);
  const recommenderSteps = buildRecommenderSteps(
    input,
    railVideos,
    markDone,
    videoStep,
  );
  const scoringStep = buildScoringStep(input);
  const evaluationsStep = buildEvaluationStep(input);

  // One collapsible "Recommenders" row rather than a dozen top-level ones: with
  // twelve of them the rail opened on ~18 rows, two-thirds of which were the
  // same shape, and read as a wall instead of a sequence.
  const recommenderGroup: TodoStep[] =
    recommenderSteps.length === 0
      ? []
      : [
          {
            id: "recommenders",
            kind: "recommenderGroup",
            phase: "watch",
            title: "Recommenders",
            href: null,
            optional: false,
            children: recommenderSteps,
            video: null,
            manual: null,
            progress: (() => {
              const rollup = containerProgress(recommenderSteps);
              return {
                fraction: rollup.fraction,
                done: rollup.done,
                label: `${rollup.doneCount}/${rollup.required.length}`,
                detail: `${rollup.doneCount} of ${rollup.required.length} complete`,
              };
            })(),
          },
        ];

  const derived = [
    ...videoSteps,
    ...recommenderGroup,
    scoringStep,
    evaluationsStep,
  ];

  const steps = derived;

  // The round total weights every required step equally — one recommender
  // counts the same as the overview video. Deliberately not weighted by
  // duration or dollars: the rail is a checklist of things to do, and a funder
  // watching a 40-minute meeting has not done four times more than one
  // watching a 10-minute one.
  //
  // A container is expanded into what it contains rather than counted as one
  // step, which is what lets "Recommenders" collapse in the UI without changing
  // what the ring means. Counting the group itself would make twelve
  // recommenders one seventh of the round instead of two thirds of it: finish
  // every one of them and the ring would move to 46% where it should reach 79%.
  // The rendering is a presentation choice; the arithmetic must not be.
  const progressUnits = steps.flatMap((step) =>
    isTodoContainer(step) ? step.children : [step],
  );
  const requiredSteps = progressUnits.filter((step) => !step.optional);
  const totalFraction = requiredSteps.reduce(
    (sum, step) => sum + step.progress.fraction,
    0,
  );

  return {
    steps,
    fraction:
      requiredSteps.length === 0 ? 0 : totalFraction / requiredSteps.length,
    requiredCount: requiredSteps.length,
    doneCount: requiredSteps.filter((step) => step.progress.done).length,
  };
}

/**
 * The ids of every step that must be expanded for `stepId` to be on screen —
 * its parent, its parent's parent, and so on. Empty when the step is top-level
 * or isn't in the rail at all.
 *
 * Used to open the group holding the funder's continuation point by default:
 * a Resume card pointing at a row nobody can see is a worse answer than one
 * extra open group.
 */
export function todoStepAncestorIds(
  steps: TodoStep[],
  stepId: string | null | undefined,
): string[] {
  if (stepId == null) {
    return [];
  }

  const walk = (nodes: TodoStep[], trail: string[]): string[] | null => {
    for (const node of nodes) {
      if (node.id === stepId) {
        return trail;
      }

      const found = walk(node.children, [...trail, node.id]);

      if (found != null) {
        return found;
      }
    }

    return null;
  };

  return walk(steps, []) ?? [];
}

/** Depth-first walk over a rail's steps and their children. */
export function flattenTodoSteps(steps: TodoStep[]): TodoStep[] {
  return steps.flatMap((step) => [step, ...flattenTodoSteps(step.children)]);
}

/** The first step with somewhere to go that isn't finished. */
export function firstUnfinishedStep(steps: TodoStep[]): TodoStep | null {
  return (
    flattenTodoSteps(steps).find(
      (step) => !step.progress.done && !step.optional && step.href != null,
    ) ?? null
  );
}

type VideoStepBuilder = (video: TodoRailVideo, optional: boolean) => TodoStep;

function buildVideoRailSteps(
  railVideos: TodoRailVideo[],
  videoStep: VideoStepBuilder,
): TodoStep[] {
  const required = railVideos.filter(
    (video) => videoKindTodoRail(video.type)?.optional === false,
  );
  const optionalByTrack = new Map<string, TodoRailVideo[]>();

  for (const video of railVideos) {
    if (videoKindTodoRail(video.type)?.optional !== true) {
      continue;
    }

    const key = video.track ?? "";
    const list = optionalByTrack.get(key) ?? [];
    list.push(video);
    optionalByTrack.set(key, list);
  }

  // Every optional kind sits at one rail position (meeting cuts share the track
  // overview's). Derived from the kind table so it can't drift from it.
  const OPTIONAL_VIDEO_RAIL_ORDER = railVideos
    .map((video) => videoKindTodoRail(video.type))
    .find((rail) => rail?.optional === true)?.order;

  const orderOf = (video: TodoRailVideo) =>
    videoKindTodoRail(video.type)?.order ?? Number.MAX_SAFE_INTEGER;
  const trackRank = (track: AssignableTrack | null) =>
    track == null ? trackOrder.length : trackOrder.indexOf(track);

  const orderedRequired = required
    // Rail position first, then canonical track order within a position, then
    // title — so two track overviews for one track keep a stable order.
    .slice()
    .sort(
      (a, b) =>
        orderOf(a) - orderOf(b) ||
        trackRank(a.track) - trackRank(b.track) ||
        compareByTitle(a, b),
    )
    // A recommender's own overview is not a step of its own: it belongs to that
    // person's group below, alongside their checkboxes.
    //
    // Keyed on the KIND, not on whether a home was resolved. An untagged
    // recommender overview also has a null home, so filtering on that promoted
    // it to a top-level REQUIRED step whose only link opens the bare
    // recommenders rail — unable to select or play it, so "Next on the list"
    // pointed at something that could never complete. A rail-only kind with no
    // addressable home belongs in no list at all.
    .filter((video) => !isRailOnlyVideoKind(video.type));

  // Which required videos each track node holds, decided up front rather than
  // one video at a time.
  //
  // A node is named after its TRACK, so it has to hold everything of that
  // track's: claiming the highlights for the first required video left a
  // track's second overview outside, sitting next to a row that claimed to be
  // the whole track.
  //
  // Membership is rail position, not track alone — in both directions, and for
  // the same reasons the cuts themselves are matched that way. A track can
  // carry more than one required video, and the round overview carries an
  // upload track while sorting first: keyed on track alone it would be swept
  // into a track node, when it belongs to the round.
  const trackNodeMembers = new Map<AssignableTrack, TodoRailVideo[]>();

  for (const video of orderedRequired) {
    const track = video.track;

    if (track == null) {
      continue;
    }

    if (orderOf(video) !== OPTIONAL_VIDEO_RAIL_ORDER) {
      continue;
    }

    // Nothing to hold, so nothing to hold it: a container over a single video
    // is a row you have to open to find one row.
    if ((optionalByTrack.get(track) ?? []).length === 0) {
      continue;
    }

    trackNodeMembers.set(track, [
      ...(trackNodeMembers.get(track) ?? []),
      video,
    ]);
  }

  const emittedTrackNodes = new Set<AssignableTrack>();

  return orderedRequired.flatMap<TodoStep>((video) => {
    const track = video.track;
    const members = track == null ? undefined : trackNodeMembers.get(track);

    // Not part of a track node — including a video whose track is unknown, and
    // one that sorts at a different rail position than the highlights.
    if (track == null || members == null || !members.includes(video)) {
      return [videoStep(video, false)];
    }

    // The node stands at the position of the first of its members; the rest
    // are inside it, not beside it.
    if (emittedTrackNodes.has(track)) {
      return [];
    }

    emittedTrackNodes.add(track);

    // The track itself is the parent, and its overview is a CHILD — a peer of
    // the highlights rather than their owner. Both are videos about the track;
    // making one of them the heading said the highlights were part of the
    // overview, which is not what they are.
    //
    // The catalog hands the highlights back in upload order, which is neither
    // meaningful nor stable — a funder reads "Meeting #1, #2, #3", so that is
    // what the rail shows.
    const children = [
      ...members.map((member) => videoStep(member, false)),
      ...(optionalByTrack.get(track) ?? [])
        .slice()
        .sort(compareByTitle)
        .map((extra) => videoStep(extra, true)),
    ];
    const rollup = containerProgress(children);

    // A collapsed track still has to say where the funder stands in it. With
    // one required video that is exactly what that video says, so the node
    // speaks with its voice rather than inventing a second phrasing for the
    // same fact — and, in particular, never turns that video's "—" for an
    // unknown length into a measured-looking 0%. A track carrying more than
    // one falls back to counting them.
    const lone = rollup.required.length === 1 ? rollup.required[0] : undefined;

    return [
      {
        id: todoStepIds.track(track),
        kind: "trackGroup",
        phase: "watch",
        title: `${trackNames[track]} Track`,
        // Like the Recommenders group: the heading opens nothing, its rows do.
        href: null,
        optional: false,
        children,
        video: null,
        manual: null,
        progress: {
          fraction: rollup.fraction,
          done: rollup.done,
          label: lone?.progress.label ?? fractionLabel(rollup.fraction),
          detail:
            lone?.progress.detail ??
            `${rollup.doneCount} of ${rollup.required.length} done`,
        },
      },
    ];
  });
}

function buildRecommenderSteps(
  input: Pick<
    BuildTodoRailInput,
    | "recommenders"
    | "linkToRecommenderNotes"
    | "linkToRecommenderProfile"
    | "linkToRecommenderScore"
  >,
  railVideos: TodoRailVideo[],
  markDone: Map<string, boolean>,
  videoStep: VideoStepBuilder,
): TodoStep[] {
  const videosByRecommender = new Map<string, TodoRailVideo[]>();

  for (const video of railVideos) {
    if (video.homeRecommenderId == null) {
      continue;
    }

    const list = videosByRecommender.get(video.homeRecommenderId) ?? [];
    list.push(video);
    videosByRecommender.set(video.homeRecommenderId, list);
  }

  return input.recommenders
    .slice()
    .sort((a, b) => a.displayName.localeCompare(b.displayName))
    .map<TodoStep>((recommender) => {
      const id = todoStepIds.recommender(recommender.recommenderId);
      const ownVideos =
        videosByRecommender.get(recommender.recommenderId) ?? [];

      const children: TodoStep[] = [
        ...ownVideos.map((video) => videoStep(video, false)),
        // Only offer "read the round notes" when there are notes to read.
        ...(recommender.hasGeneralNotes
          ? [
              observedStep({
                id: todoStepIds.recommenderNotes(recommender.recommenderId),
                title: "Read general round notes",
                phase: "watch",
                // The recommender's page, where the notes are. A required step
                // with no destination is a dead end: "Next on the list" skips
                // anything unopenable and the row itself is inert, so an unread
                // mark could hold the round below 100% with nothing on screen
                // the funder could click to resolve it.
                href: input.linkToRecommenderNotes(recommender.recommenderId),
                settable: true,
                done:
                  markDone.get(
                    todoStepIds.recommenderNotes(recommender.recommenderId),
                  ) ?? false,
              }),
            ]
          : []),
        ...(recommender.hasProfileSummary
          ? [
              observedStep({
                id: todoStepIds.recommenderProfile(recommender.recommenderId),
                title: "Viewed profile summary",
                phase: "watch",
                // Same reasoning as the notes step above: reachable, or it
                // is a required step nothing can resolve. Anchored on the
                // profile card, not merely the page — dropping the funder at
                // the top of a long recommender page hands back the "find it"
                // work the step is asking them to do.
                href: input.linkToRecommenderProfile(recommender.recommenderId),
                settable: true,
                done:
                  markDone.get(
                    todoStepIds.recommenderProfile(recommender.recommenderId),
                  ) ?? false,
              }),
            ]
          : []),
        // Derived, not stored: the funder's own ratings already answer it.
        // Unlike the two observed marks this one is ACTIONABLE, so it carries
        // the recommender's page as its destination — otherwise a group could
        // sit incomplete with nothing in it the funder could click to finish.
        observedStep({
          id: todoStepIds.recommenderScore(recommender.recommenderId),
          title: "Set a score",
          phase: "watch",
          href: input.linkToRecommenderScore(recommender.recommenderId),
          // Derived from the funder's actual ratings — asserting it by hand
          // would claim a score that isn't there.
          settable: false,
          done: recommender.hasScore,
        }),
      ];

      const doneChildren = children.filter(
        (child) => child.progress.done,
      ).length;
      const fraction =
        children.length === 0 ? 1 : doneChildren / children.length;

      return {
        id,
        kind: "recommender",
        phase: "watch",
        title: recommender.displayName,
        // The group header itself opens nothing — its rows do. A header link
        // would be a second, subtly different destination for the same thing.
        href: null,
        optional: false,
        children,
        video: null,
        manual: null,
        progress: {
          fraction,
          done: doneChildren === children.length,
          label: `${doneChildren}/${children.length}`,
          detail: `${doneChildren} of ${children.length} done`,
        },
      };
    });
}

function buildScoringStep(
  input: Pick<BuildTodoRailInput, "scoring" | "linkToScoring">,
): TodoStep {
  // Ask for the target, or for everything when the round has less than that.
  const scoringTarget = Math.min(TODO_SCORING_TARGET, input.scoring.rateable);
  const scoringFraction =
    scoringTarget > 0 ? Math.min(1, input.scoring.rated / scoringTarget) : 0;
  return {
    id: todoStepIds.scoring,
    kind: "scoring",
    phase: "score",
    title: "Scoring calibration",
    href: input.linkToScoring(),
    optional: false,
    children: [],
    video: null,
    manual: null,
    progress: {
      fraction: scoringTarget === 0 ? 1 : scoringFraction,
      // Nothing to score is VACUOUSLY complete. Left as "not done" it was a
      // required unit that could never finish, so the round could not reach
      // 100% and "Next on the list" pointed at a step with no work in it.
      done: scoringTarget === 0 || input.scoring.rated >= scoringTarget,
      label: `${Math.min(input.scoring.rated, scoringTarget)}/${scoringTarget}`,
      detail:
        scoringTarget === 0
          ? "Nothing to score yet"
          : `${Math.max(0, scoringTarget - input.scoring.rated)} left of ${scoringTarget}`,
    },
  };
}

function buildEvaluationStep(
  input: Pick<
    BuildTodoRailInput,
    "evaluations" | "linkToEvaluations" | "formatCurrency"
  >,
): TodoStep {
  const allocated = input.evaluations.allocated;
  return {
    id: todoStepIds.evaluations,
    kind: "evaluations",
    phase: "allocate",
    title: "Evaluations",
    href: input.linkToEvaluations(),
    optional: false,
    children: [],
    video: null,
    manual: null,
    progress: {
      // Binary, because there is no target: the step is unstarted until the
      // funder has placed something, and done once they have. Deciding they
      // have allocated ENOUGH is their call, not a number the round holds.
      fraction: allocated > 0 ? 1 : 0,
      done: allocated > 0,
      label: input.formatCurrency(allocated),
      detail:
        allocated > 0
          ? `${input.formatCurrency(allocated)} allocated`
          : "Nothing allocated yet",
    },
  };
}
